/**
 * How long a chat has grown, in characters of the last model request (svc-reporting's
 * `contextChars`, the same number it logs as `inputChars`). Mirrors LONG_CHAT_SOFT_CHARS and
 * LONG_CHAT_HARD_CHARS in svc-reporting's le-data/long-chat.ts.
 *
 * Datadog, 5 to 13 Oct 2026: model calls under 250K chars took 4s at the median and 0.8% did not
 * finish; 1M to 2M took 15s and 3.6% did not finish; over 2M took 19s and about 15% did not finish.
 */
export const LONG_CHAT_SOFT_CHARS = 1_000_000;
export const LONG_CHAT_HARD_CHARS = 2_000_000;

export type LongChatLevel = 0 | 1 | 2;

export function longChatLevel(contextChars: number | undefined | null): LongChatLevel {
  if (typeof contextChars !== 'number' || !Number.isFinite(contextChars)) {
    return 0;
  }
  if (contextChars >= LONG_CHAT_HARD_CHARS) {
    return 2;
  }
  return contextChars >= LONG_CHAT_SOFT_CHARS ? 1 : 0;
}

const SYDNEY = 'Australia/Sydney';

/** The Sydney calendar day of an instant, as YYYY-MM-DD. */
export function sydneyDayKey(at: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: SYDNEY, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    at,
  );
}

/** "Mon 6 Oct", in Sydney time. */
export function formatSydneyDay(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: SYDNEY,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('weekday')} ${part('day')} ${part('month')}`;
}

/** Whether an instant fell on an earlier Sydney calendar day than `now`. */
export function isBeforeSydneyToday(at: Date, now: Date): boolean {
  return sydneyDayKey(at) < sydneyDayKey(now);
}

const NOT_NOW_KEY_PREFIX = 'askrichard.longChat.notNow.';

/** The reader chose Not now on this chat today (Sydney). Level 1 comes back the next day. */
export function isLongChatSnoozed(sessionId: string, now: Date): boolean {
  try {
    return window.localStorage.getItem(`${NOT_NOW_KEY_PREFIX}${sessionId}`) === sydneyDayKey(now);
  } catch {
    return false;
  }
}

export function snoozeLongChat(sessionId: string, now: Date): void {
  try {
    window.localStorage.setItem(`${NOT_NOW_KEY_PREFIX}${sessionId}`, sydneyDayKey(now));
  } catch {
    // Storage unavailable: the notice hides for this view only.
  }
}

/** Where each surface keeps its own new chat control. */
export type NewChatSurface = 'pane' | 'page';

export function longChatSoftText(): string {
  return 'This chat is long, so answers may slow down.';
}

export function longChatHardText(): string {
  return 'This chat is too long, so answers may fail.';
}
